import React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { getTrainingVideoEmbedUrl } from '../../services/trainingsService'
import type { TrainingVideo } from '../../types/Training/TrainingVideo'

type TutorialPlayerDialogProps = {
	open: boolean
	tutorial: TrainingVideo | null
	onClose: () => void
}

const TutorialPlayerDialog = ({ open, tutorial, onClose }: TutorialPlayerDialogProps) => {
	const [embedUrl, setEmbedUrl] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)

	useEffect(() => {
		if (!open || !tutorial) {
			setEmbedUrl('')
			setErrorMessage(null)
			setIsLoading(false)
			return
		}

		let isMounted = true

		const loadEmbedUrl = async () => {
			setIsLoading(true)
			setErrorMessage(null)
			try {
				const response = await getTrainingVideoEmbedUrl(tutorial.id)
				if (isMounted) {
					setEmbedUrl(response.embedUrl)
				}
			} catch {
				if (isMounted) {
					setEmbedUrl('')
					setErrorMessage('Unable to load this tutorial video.')
				}
			} finally {
				if (isMounted) {
					setIsLoading(false)
				}
			}
		}

		void loadEmbedUrl()

		return () => {
			isMounted = false
		}
	}, [open, tutorial])

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth='lg'
			scroll='paper'
			aria-labelledby='tutorial-player-dialog-title'
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					height: 'min(88dvh, calc(100dvh - 24px))',
					m: 1.5,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				},
			}}
		>
			<DialogTitle id='tutorial-player-dialog-title' sx={{ pr: 6 }}>
				{tutorial?.title}
				<IconButton
					aria-label='Close tutorial player'
					onClick={onClose}
					sx={{ position: 'absolute', right: 12, top: 12 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent
				dividers
				sx={{
					p: 2,
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					overflow: 'hidden',
				}}
			>
				{tutorial?.description && (
					<Typography
						variant='body2'
						color='text.secondary'
						sx={{ mb: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
					>
						{tutorial.description}
					</Typography>
				)}

				<Box
					sx={{
						width: '100%',
						flex: 1,
						minHeight: 0,
						backgroundColor: 'grey.100',
						borderRadius: 1,
						overflow: 'hidden',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					{isLoading && (
						<Stack spacing={1} alignItems='center'>
							<CircularProgress size={26} />
							<Typography variant='body2' color='text.secondary'>
								Loading tutorial...
							</Typography>
						</Stack>
					)}

					{!isLoading && errorMessage && (
						<Typography variant='body2' color='error.main'>
							{errorMessage}
						</Typography>
					)}

					{!isLoading && !errorMessage && open && embedUrl && (
						<Box
							component='iframe'
							src={embedUrl}
							title={tutorial ? `${tutorial.title} tutorial video` : 'Tutorial video'}
							allow='clipboard-write; fullscreen'
							sandbox='allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation'
							loading='lazy'
							referrerPolicy='unsafe-url'
							allowFullScreen
							sx={{ border: 0, width: '100%', height: '100%', borderRadius: '10px' }}
						/>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	)
}

export default TutorialPlayerDialog
